import {client} from '@/utils/gql'
import {NotFoundError, ServerError} from '@/utils/server/errors'
import {WithRequiredProp} from '@/utils/types'

import {
  Brand_Bool_Exp,
  Brand_Insert_Input,
  Brand_Order_By,
  Brand_Set_Input,
  BrandFragment,
} from '../graphqlSdk'

const getByIdOrNull = async (id: string, deleted: boolean = false): Promise<BrandFragment | null> =>
  (await client.brandGetById({id, deleted}))?.brand?.[0] || null

const getById = async (id: string, deleted: boolean = false): Promise<BrandFragment> => {
  const brand = await getByIdOrNull(id, deleted)

  if (!brand) {
    throw new NotFoundError(`Brand by id returned null`, {id})
  }

  return brand
}

const getMany = async (
  filter?: Brand_Bool_Exp,
  order_by?: Brand_Order_By,
): Promise<BrandFragment[]> =>
  (
    await client.brandGetMany({
      filter: {deleted: {_eq: false}, ...filter},
      order_by,
    })
  ).brand

const insertOne = async (
  object: WithRequiredProp<Brand_Insert_Input, 'created_by_user_id'>,
): Promise<BrandFragment> => {
  const response = (await client.brandInsertOne({object})).insert_brand_one

  if (!response) {
    throw new ServerError('brand insert returned null', {object})
  }

  return response
}

const insertMany = async (
  objects: WithRequiredProp<Brand_Insert_Input, 'created_by_user_id'>[],
): Promise<BrandFragment[]> => {
  const response = (await client.brandInsertMany({objects}))?.insert_brand?.returning

  if (!response) {
    throw new ServerError('brand insert many returned null', {objects})
  }

  return response
}

const updateOne = async (
  id: string,
  changes: WithRequiredProp<Brand_Set_Input, 'updated_by_user_id'>,
): Promise<BrandFragment> => {
  const brand = (await client.brandUpdateOne({id, changes})).update_brand_by_pk

  if (!brand) {
    throw new ServerError('brand update returned null', {id, changes})
  }

  return brand
}

const deleteOne = async (id: string): Promise<BrandFragment> => {
  const brand = (await client.brandDeleteOne({id})).delete_brand_by_pk
  if (!brand) {
    throw new ServerError('Delete brand by id returned empty result', {id, brand})
  }

  return brand
}

export const brandOrm = {
  getByIdOrNull,
  getById,
  getMany,
  insertOne,
  insertMany,
  updateOne,
  deleteOne,
}
